import React from 'react'
import {Box} from '@mui/material'
import {TimeslotProps} from 'src/types/types'
import Card from 'src/components-new/layout/Card'
import PasswordFieldNew from 'src/components-new/inputs/PasswordFieldNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import {AddressValue} from '../selfRegistrationValidation'
import AddressFields from './AddressFields'
import TimeSlotsField from './TimeSlotsField'

export interface PersonalDataStepValue {
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
  fiscalCode: string
  cellPhoneNumber: string
  address: AddressValue
  timeSlots: TimeslotProps[]
}

interface PersonalDataStepProps {
  value: PersonalDataStepValue
  onChange: (value: PersonalDataStepValue) => void
  email: string
  errors: Record<string, string>
  disabled?: boolean
}

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
  gap: 2,
} as const

/**
 * Step 2/3 — orchestrator for the "Dati principali" / "Indirizzo" / "Fasce orarie
 * preferite" cards that make up the legacy inner step 0 of
 * src/pages/selfRegistration/steps/SecondStep/SecondStep.tsx (lines 193-397): all three
 * were already rendered together on one screen there (just as 3 stacked antd
 * `CardCustom`s under one shared "Avanti" button), so this keeps the same grouping — only
 * the outer step boundary (Account / Dati e indirizzo / Consensi) is new.
 */
export default function PersonalDataStep({value, onChange, email, errors, disabled}: PersonalDataStepProps): JSX.Element {
  const setField = <K extends keyof PersonalDataStepValue>(key: K, next: PersonalDataStepValue[K]) => {
    onChange({...value, [key]: next})
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <Card title="Dati principali">
        <Box sx={GRID_SX}>
          <TextFieldNew
            label="Nome"
            value={value.firstName}
            onChange={(v) => setField('firstName', v)}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
          />
          <TextFieldNew
            label="Cognome"
            value={value.lastName}
            onChange={(v) => setField('lastName', v)}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
          />
          <PasswordFieldNew
            label="Password"
            value={value.password}
            onChange={(v) => setField('password', v)}
            autoComplete="new-password"
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.password)}
            helperText={errors.password}
          />
          <PasswordFieldNew
            label="Conferma password"
            value={value.confirmPassword}
            onChange={(v) => setField('confirmPassword', v)}
            autoComplete="new-password"
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword}
          />
          <TextFieldNew
            label="Codice fiscale"
            value={value.fiscalCode}
            onChange={(v) => setField('fiscalCode', v)}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.fiscalCode)}
            helperText={errors.fiscalCode}
          />
          <TextFieldNew
            label="Numero cellulare"
            type="tel"
            value={value.cellPhoneNumber}
            onChange={(v) => setField('cellPhoneNumber', v)}
            required
            fullWidth
            disabled={disabled}
            error={Boolean(errors.cellPhoneNumber)}
            helperText={errors.cellPhoneNumber}
          />
          <TextFieldNew label="Email" type="email" value={email} onChange={() => undefined} disabled fullWidth />
        </Box>
      </Card>

      <Card title="Indirizzo">
        <AddressFields
          value={value.address}
          onChange={(address) => setField('address', address)}
          errors={errors}
          disabled={disabled}
        />
      </Card>

      <Card title="Fasce orarie preferite">
        <TimeSlotsField
          value={value.timeSlots}
          onChange={(timeSlots) => setField('timeSlots', timeSlots)}
          error={errors.timeSlots}
          disabled={disabled}
        />
      </Card>
    </Box>
  )
}
